'use client';

import Image from 'next/image';
import React from 'react';

export default function TermsAndConditionsPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between py-6">
            <div className="flex items-center">
              <Image
                alt="Zone"
                className="h-10 w-auto"
                height={40}
                src="/images/erun-logo.svg"
                width={120}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="mb-4 text-4xl font-bold">Terms and Conditions</h1>
            <p className="mx-auto max-w-2xl text-xl text-blue-100">
              Please read these terms carefully before using Zone, your virtual run companion.
            </p>
          </div>
        </div>
      </div>

      {/* Terms Content */}
      <div className="mx-auto max-w-4xl px-4 py-12 sm:px-6 lg:px-8">
        <div className="rounded-lg bg-white p-8 shadow-md">
          <div className="prose prose-lg max-w-none">
            <p className="mb-8 text-lg text-gray-600">
              <strong>Effective Date:</strong> July 26, 2025
            </p>

            <p className="mb-6">
              Welcome to Zone, your virtual run companion. By using the app, you agree to the
              following Terms and Conditions. Please read carefully.
            </p>

            <h2 className="mb-4 mt-8 text-2xl font-bold text-gray-900">1. Eligibility</h2>
            <p className="mb-6">
              By registering for Zone, you confirm that the information you provide is accurate and
              truthful.
            </p>

            <h2 className="mb-4 mt-8 text-2xl font-bold text-gray-900">2. Use of the App</h2>
            <ul className="mb-6 list-disc space-y-2 pl-6">
              <li>
                Zone is intended for personal use to track virtual runs and receive participation
                rewards.
              </li>
              <li>
                Users are responsible for ensuring they are in good health before engaging in
                physical activity.
              </li>
              <li>
                All runs must be completed honestly. Falsified data may result in disqualification
                or account suspension.
              </li>
            </ul>

            <h2 className="mb-4 mt-8 text-2xl font-bold text-gray-900">
              3. Event Participation and Rewards
            </h2>
            <ul className="mb-6 list-disc space-y-2 pl-6">
              <li>
                Runs must follow the event&apos;s rules (e.g., distance, date, and format) to be
                valid.
              </li>
              <li>
                Verified participants will receive physical rewards such as medals and finisher
                shirts.
              </li>
              <li>
                Zone reserves the right to withhold rewards if suspicious activity or data
                manipulation is detected.
              </li>
            </ul>

            <h2 className="mb-4 mt-8 text-2xl font-bold text-gray-900">4. User Conduct</h2>
            <p className="mb-4">You agree not to:</p>
            <ul className="mb-6 list-disc space-y-2 pl-6">
              <li>Falsify or manipulate activity data.</li>
              <li>Upload harmful, illegal, or offensive content.</li>
              <li>
                Violate other users&apos; privacy or interfere with the app&apos;s operations.
              </li>
            </ul>

            <h2 className="mb-4 mt-8 text-2xl font-bold text-gray-900">5. Intellectual Property</h2>
            <p className="mb-6">
              All designs, logos, and content within Zone are the property of Zone and may not be
              reused or reproduced without permission.
            </p>

            <h2 className="mb-4 mt-8 text-2xl font-bold text-gray-900">
              6. Account Suspension or Termination
            </h2>
            <p className="mb-4">We may suspend or terminate accounts that:</p>
            <ul className="mb-6 list-disc space-y-2 pl-6">
              <li>Violate these terms.</li>
              <li>Engage in fraudulent or abusive behavior.</li>
              <li>Disrupt the safety or experience of other users.</li>
            </ul>

            <h2 className="mb-4 mt-8 text-2xl font-bold text-gray-900">
              7. Disclaimer and Liability
            </h2>
            <ul className="mb-6 list-disc space-y-2 pl-6">
              <li>
                Participation is voluntary. Zone is not liable for any injuries, losses, or damages
                that may occur while using the app or during physical activity.
              </li>
              <li>Services are provided &quot;as is&quot; without any guarantees or warranties.</li>
            </ul>

            <h2 className="mb-4 mt-8 text-2xl font-bold text-gray-900">8. Changes to Terms</h2>
            <p className="mb-6">
              Zone may revise these terms at any time. Continued use of the app after changes means
              you agree to the updated terms.
            </p>

            <h2 className="mb-4 mt-8 text-2xl font-bold text-gray-900">9. Contact</h2>
            <p className="mb-6">
              For questions or support, contact us at:
              <br />
              📧{' '}
              <a className="text-blue-600 hover:text-blue-700" href="mailto:van@findmyshots.com">
                van@findmyshots.com
              </a>
            </p>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-800 py-12 text-white">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
            <div>
              <h3 className="mb-4 text-lg font-semibold">Zone</h3>
              <p className="mb-4 text-gray-300">
                Join virtual running challenges and achieve your fitness goals with our supportive
                community.
              </p>
              <div className="flex space-x-4">
                <a
                  className="text-gray-300 transition-colors hover:text-white"
                  href="https://www.facebook.com/findmyshots"
                >
                  Facebook
                </a>
              </div>
            </div>

            <div>
              <h3 className="mb-4 text-lg font-semibold">Quick Links</h3>
              <ul className="space-y-2">
                <li>
                  <a className="text-gray-300 transition-colors hover:text-white" href="/support">
                    Support
                  </a>
                </li>
                <li>
                  <a
                    className="text-gray-300 transition-colors hover:text-white"
                    href="/terms-and-conditions"
                  >
                    Terms and Conditions
                  </a>
                </li>
                <li>
                  <a
                    className="text-gray-300 transition-colors hover:text-white"
                    href="/privacy-policy"
                  >
                    Privacy Policy
                  </a>
                </li>
              </ul>
            </div>

            <div>
              <h3 className="mb-4 text-lg font-semibold">Contact Info</h3>
              <ul className="space-y-2 text-gray-300">
                <li>Email: van@findmyshots.com</li>
                <li>Response time: Within 24 hours</li>
                <li>Business hours: Mon-Fri 9AM-6PM</li>
              </ul>
            </div>
          </div>

          <div className="mt-8 border-t border-gray-700 pt-8 text-center text-gray-300">
            <p>&copy; 2025 Zone. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
